import React, { useState, useRef, useEffect } from "react";
import useTheme from "../ColorConfig/usetheme";
import Readytostart from "../Products/readytostart";
import GetInTouchForm from "../GetinTouchform/GetinTouch";
import SEO from "../../AASeo/Seo";

// Import subcomponents
import HeroSection from "./SubAbout/Herosection";
import OurStorySection from "./SubAbout/OurStory";
import WhyChooseSection from "./SubAbout/WhyChoose";
import TeamCultureSection from "./SubAbout/TeamCulture";
import StatisticsSection from "./SubAbout/Statistics";
import ClientsSection from "./SubAbout/ClientSec";

const organization = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "IDLBAY Private Limited",
  alternateName: "IDLBAY",
  url: "https://idlbay.com",
  logo: "https://idlbay.com/idlbaylogo.png",
  sameAs: [
    "https://www.linkedin.com/company/idlbay/",
    "https://x.com/idlbayPvtLtd",
    "https://www.instagram.com/idlbay.official?igsh=dnIxMm50cmdxNDNy",
    "https://www.facebook.com/profile.php?id=61582746578911",
    "https://www.youtube.com/channel/UCDSb9__-mSLqeyuZYelakcw",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+91-9840841840",
    contactType: "Customer Support",
    areaServed: "IN",
    availableLanguage: ["English", "Tamil"],
  },
  address: {
    "@type": "PostalAddress",
    streetAddress:
      "Office No.1, First Floor, Door No.1, B Narasimhan 3rd Street",
    addressLocality: "T-Nagar, Chennai",
    addressRegion: "Tamil Nadu",
    postalCode: "600017",
    addressCountry: "IN",
  },
};

const AboutUs = () => {
  const colors = useTheme();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showGetInTouch, setShowGetInTouch] = useState(false);
  const [showFullIntro, setShowFullIntro] = useState(false);
  const sectionRef = useRef(null);

  // Animation visibility states
  const [visibleSections, setVisibleSections] = useState({});
  const observerRef = useRef(null);

  const handleContactClick = (e) => {
    e.preventDefault();
    setShowGetInTouch(true);
    document.body.style.overflow = "hidden";
  };

  const closeGetInTouch = () => {
    setShowGetInTouch(false);
    document.body.style.overflow = "auto";
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    // Intersection Observer for animations
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => ({
              ...prev,
              [entry.target.dataset.section]: true,
            }));
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: "-50px 0px",
      }
    );

    // Observe all animated sections
    const sections = document.querySelectorAll("[data-section]");
    sections.forEach((section) => {
      observerRef.current.observe(section);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const scrollToIntro = () => {
    if (sectionRef.current) {
      const headerOffset = 100;
      const elementPosition = sectionRef.current.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const toggleIntro = () => {
    setShowFullIntro(!showFullIntro);
  };

  // Fast animation styles with reduced durations
  const animationStyles = `
    /* Fast video animation from bottom with quick slide up */
    @keyframes videoSlideUpFromBottom {
      0% {
        opacity: 0;
        transform: translateY(150px) scale(0.95);
        filter: blur(8px);
      }
      100% {
        opacity: 1;
        transform: translateY(0) scale(1);
        filter: blur(0px);
      }
    }

    /* Video container with fast animation */
    .video-slide-in {
      opacity: 0;
      transform: translateY(150px) scale(0.95);
      filter: blur(8px);
      animation: videoSlideUpFromBottom 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s forwards;
      will-change: transform, opacity, filter;
      backface-visibility: hidden;
    }

    /* Video overlay quick fade in */
    @keyframes overlaySlideIn {
      0% {
        opacity: 0;
        transform: translateY(50px);
      }
      100% {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .video-overlay-animate {
      opacity: 0;
      transform: translateY(50px);
      animation: overlaySlideIn 0.6s ease-out 0.2s forwards;
    }

    /* Fast hero section animations */
    @keyframes heroTitleSlideUp {
      0% {
        opacity: 0;
        transform: translateY(80px);
      }
      100% {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes heroDescSlideUp {
      0% {
        opacity: 0;
        transform: translateY(60px);
      }
      100% {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes heroButtonsSlideUp {
      0% {
        opacity: 0;
        transform: translateY(40px);
      }
      100% {
        opacity: 1;
        transform: translateY(0);
      }
    }

    /* Fast hero animation classes */
    .hero-title {
      opacity: 0;
      transform: translateY(80px);
      animation: heroTitleSlideUp 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s forwards;
    }

    .hero-description {
      opacity: 0;
      transform: translateY(60px);
      animation: heroDescSlideUp 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s forwards;
      line-height: 1.6 !important;
    }

    .hero-buttons {
      opacity: 0;
      transform: translateY(40px);
      animation: heroButtonsSlideUp 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.3s forwards;
    }

    /* Performance optimizations for hero animations */
    .hero-title,
    .hero-description,
    .hero-buttons {
      will-change: transform, opacity;
      backface-visibility: hidden;
      transform-style: preserve-3d;
    }
    
    .hero-title{
      margin-top: 80px !important;
    }

    /* Fast scroll animations */
    @keyframes slideInFromBottom {
      0% {
        opacity: 0;
        transform: translate3d(0, 60px, 0);
      }
      100% {
        opacity: 1;
        transform: translate3d(0, 0, 0);
      }
    }

    /* Fast fade in from bottom animation */
    @keyframes fadeInUp {
      0% {
        opacity: 0;
        transform: translate3d(0, 40px, 0);
      }
      100% {
        opacity: 1;
        transform: translate3d(0, 0, 0);
      }
    }

    /* Fast scale up animation */
    @keyframes scaleUp {
      0% {
        opacity: 0;
        transform: scale(0.8) translateY(20px);
      }
      100% {
        opacity: 1;
        transform: scale(1) translateY(0);
      }
    }

    /* Fast initial hidden state */
    .animate-on-scroll {
      opacity: 0;
      transform: translateY(60px);
      transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    }

    /* Fast visible state with animation */
    .animate-on-scroll.is-visible {
      opacity: 1;
      transform: translateY(0);
    }

    /* Fast smooth animation class */
  .smooth-reveal {
  opacity: 0;
  transform: translateY(50px);
  transition: opacity 0.4s ease-out, transform 0.4s ease-out;
}

.smooth-reveal.revealed {
  opacity: 1;
  transform: translateY(0);
}

/* Specifically target h6 elements inside smooth-reveal */
.smooth-reveal h6 {
  line-height: 1.6 !important;
  margin-bottom: 1rem !important;
}

.smooth-reveal.revealed h6 {
  line-height: 1.6 !important;
}

    /* Fast stagger delays for multiple elements */
    .delay-100 { transition-delay: 0.05s; }
    .delay-200 { transition-delay: 0.1s; }
    .delay-300 { transition-delay: 0.15s; }
    .delay-400 { transition-delay: 0.2s; }
    .delay-500 { transition-delay: 0.25s; }
    .delay-600 { transition-delay: 0.3s; }

    /* Fast card hover effects */
    .card-hover-effect {
      transition: all 0.2s ease;
    }

    .card-hover-effect:hover {
      transform: translateY(-5px);
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
    }

    /* Fast image hover zoom */
    .image-hover-zoom {
      overflow: hidden;
      transition: transform 0.2s ease;
    }

    .image-hover-zoom img {
      transition: transform 0.2s ease;
    }

    .image-hover-zoom:hover img {
      transform: scale(1.05);
    }

    /* Fast button animations */
    .btn-animated {
      transition: all 0.2s ease;
      transform: translateZ(0);
    }

    .btn-animated:hover {
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
    }

    /* Performance optimizations */
    .performance-optimized {
      transform: translateZ(0);
      backface-visibility: hidden;
      will-change: transform;
    }

    /* Fast custom animation classes for different elements */
    .slide-up {
      opacity: 0;
      transform: translateY(60px);
      transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    }

    .slide-up.active {
      opacity: 1;
      transform: translateY(0);
    }

    .fade-in {
      opacity: 0;
      transition: opacity 0.4s ease-out;
    }

    .fade-in.active {
      opacity: 1;
    }

    .scale-in {
      opacity: 0;
      transform: scale(0.9);
      transition: all 0.3s ease-out;
    }

    .scale-in.active {
      opacity: 1;
      transform: scale(1);
    }

    /* Mobile responsive adjustments */
    @media (max-width: 768px) {
      .hero-section-mobile {
        padding-bottom: 60px !important;
      }
      
      .hero-title-mobile {
        font-size: 2.5rem !important;
        line-height: 1.2 !important;
        margin-bottom: 1rem !important;
        margin-top: 50px !important;
      }
      
      .hero-description-mobile {
        font-size: 1rem !important;
        line-height: 1.5 !important;
        margin-bottom: 1.5rem !important;
      }
      
      .hero-buttons-mobile {
        flex-direction: row !important;
        gap: 0.5rem !important;
        justify-content: start !important;
      }
      
      .hero-button-mobile {
        padding: 0.5rem 1rem !important;
        font-size: 0.875rem !important;
        flex: 1 !important;
        max-width: 150px !important;
      }
      
      .cards-mobile-two-per-row {
        display: grid !important;
        grid-template-columns: 1fr 1fr !important;
        gap: 1rem !important;
      }
      
      .card-mobile-compact {
        padding: 1.5rem 0.75rem !important;
      }
      
      .card-icon-mobile {
        width: 60px !important;
        height: 60px !important;
        margin-bottom: 0.75rem !important;
      }
      
      .card-title-mobile {
        font-size: 1rem !important;
        margin-bottom: 0.75rem !important;
      }
      
      .card-text-mobile h5 {
        font-size: 0.85rem !important;
        line-height: 1.4 !important;
      }
        .aboutdes h6{
        
      line-height: 1.7 !important;
}

      .cardtextdesktop{
       font-size: 0.55rem !important;
        line-height: 1.4 !important;
      }

      /* Faster mobile animations */
      .hero-title,
      .hero-description,
      .hero-buttons {
        animation-duration: 0.4s !important;
      }
      
      .animate-on-scroll,
      .smooth-reveal,
      .slide-up,
      .fade-in,
      .scale-in {
        transition-duration: 0.3s !important;
      }
    }
  `;

  return (
    <>
      <SEO
        title="About Us | IDLBAY - Our Story, Team & Values"
        description="Learn about IDLBAY's journey, our passionate team, and the values that drive us to deliver top IT solutions and digital services."
        keywords="About IDLBAY, our story, team, company values, IT company, digital services, Chennai"
        url="https://idlbay.com/about"
        organization={organization}
      />
      <style>{animationStyles}</style>
      <div style={{ backgroundColor: colors.white }}>
        {/* Hero Section */}
        <HeroSection
          colors={colors}
          handleContactClick={handleContactClick}
          scrollToIntro={scrollToIntro}
        />

        {/* Our Story Section */}
        <OurStorySection
          colors={colors}
          sectionRef={sectionRef}
          visibleSections={visibleSections}
          showFullIntro={showFullIntro}
          toggleIntro={toggleIntro}
        />

        {/* Why Choose IDLBAY Section */}
        <WhyChooseSection colors={colors} visibleSections={visibleSections} />

        {/* Team Culture Section */}
        <TeamCultureSection colors={colors} visibleSections={visibleSections} />

        {/* Statistics Section */}
        <StatisticsSection colors={colors} visibleSections={visibleSections} />

        {/* Clients Section */}
        <ClientsSection colors={colors} visibleSections={visibleSections} />

        <Readytostart />

        {showGetInTouch && (
          <div className="modal-overlay" onClick={closeGetInTouch}>
            <div
              className="modal-content-custom"
              onClick={(e) => e.stopPropagation()}
            >
              <button className="close-button" onClick={closeGetInTouch}>
                &times;
              </button>
              <GetInTouchForm isModal={true} />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AboutUs;
